class ClayProductShowcase extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    if (this.dataset.deferUntilVisible === "true" && !this.deferredReady) {
      this.visibilityObserver = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.deferredReady = true;
        this.visibilityObserver.disconnect();
        this.connectedCallback();
      });
      this.visibilityObserver.observe(this);
      return;
    }

    this.initialized = true;
    this.viewport = this.querySelector("[data-showcase-viewport]");
    this.track = this.querySelector("[data-showcase-track]");
    this.section = this.closest("[data-clay-carousel-section]");
    this.previousButton = this.section?.querySelector("[data-showcase-previous]");
    this.nextButton = this.section?.querySelector("[data-showcase-next]");
    if (!this.viewport || !this.track) return;

    this.onPrevious = () => this.scrollByStep(-1);
    this.onNext = () => this.scrollByStep(1);
    this.onScroll = () => this.requestControlUpdate();
    this.onKeyDown = (event) => this.handleKeyDown(event);
    this.onPointerDown = (event) => this.startPointer(event);
    this.onPointerMove = (event) => this.movePointer(event);
    this.onPointerUp = (event) => this.endPointer(event);
    this.onClick = (event) => this.preventDraggedClick(event);
    this.onDragStart = (event) => event.preventDefault();

    this.previousButton?.addEventListener("click", this.onPrevious);
    this.nextButton?.addEventListener("click", this.onNext);
    this.viewport.addEventListener("scroll", this.onScroll, { passive: true });
    this.viewport.addEventListener("keydown", this.onKeyDown);
    this.viewport.addEventListener("pointerdown", this.onPointerDown);
    this.viewport.addEventListener("pointermove", this.onPointerMove);
    this.viewport.addEventListener("pointerup", this.onPointerUp);
    this.viewport.addEventListener("pointercancel", this.onPointerUp);
    this.viewport.addEventListener("click", this.onClick, true);
    this.viewport.addEventListener("dragstart", this.onDragStart);

    this.resizeObserver = new ResizeObserver(() => this.updateControls());
    this.resizeObserver.observe(this.viewport);
    this.updateControls();
    this.initializeProducts();
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.controlFrame);
    clearTimeout(this.clickReset);
    this.visibilityObserver?.disconnect();
    this.resizeObserver?.disconnect();
    this.previousButton?.removeEventListener("click", this.onPrevious);
    this.nextButton?.removeEventListener("click", this.onNext);
    this.viewport?.removeEventListener("scroll", this.onScroll);
    this.viewport?.removeEventListener("keydown", this.onKeyDown);
    this.viewport?.removeEventListener("pointerdown", this.onPointerDown);
    this.viewport?.removeEventListener("pointermove", this.onPointerMove);
    this.viewport?.removeEventListener("pointerup", this.onPointerUp);
    this.viewport?.removeEventListener("pointercancel", this.onPointerUp);
    this.viewport?.removeEventListener("click", this.onClick, true);
    this.viewport?.removeEventListener("dragstart", this.onDragStart);
  }

  get slides() {
    return [...this.track.querySelectorAll("[data-showcase-slide]")];
  }

  get step() {
    const slide = this.slides[0];
    if (!slide) return this.viewport.clientWidth;
    const styles = getComputedStyle(this.track);
    const gap = parseFloat(styles.columnGap || styles.gap) || 0;
    return slide.getBoundingClientRect().width + gap;
  }

  scrollByStep(direction) {
    this.viewport.scrollBy({
      left: direction * this.step,
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ? "auto"
        : "smooth",
    });
  }

  handleKeyDown(event) {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
    event.preventDefault();
    this.scrollByStep(event.key === "ArrowRight" ? 1 : -1);
  }

  startPointer(event) {
    if (event.pointerType !== "mouse" || event.button !== 0) return;
    this.dragging = true;
    this.dragMoved = false;
    this.pointerStart = event.clientX;
    this.scrollStart = this.viewport.scrollLeft;
  }

  movePointer(event) {
    if (!this.dragging) return;
    const distance = event.clientX - this.pointerStart;

    if (Math.abs(distance) > 4 && !this.dragMoved) {
      this.dragMoved = true;
      this.viewport.setPointerCapture(event.pointerId);
      this.viewport.classList.add("is-dragging");
    }

    if (!this.dragMoved) return;
    this.viewport.scrollLeft = this.scrollStart - distance;
    event.preventDefault();
  }

  endPointer(event) {
    if (!this.dragging) return;
    this.dragging = false;
    this.viewport.classList.remove("is-dragging");

    if (this.viewport.hasPointerCapture(event.pointerId)) {
      this.viewport.releasePointerCapture(event.pointerId);
    }

    clearTimeout(this.clickReset);
    this.clickReset = setTimeout(() => {
      this.dragMoved = false;
    }, 0);
  }

  preventDraggedClick(event) {
    if (!this.dragMoved) return;
    event.preventDefault();
    event.stopPropagation();
    this.dragMoved = false;
  }

  requestControlUpdate() {
    cancelAnimationFrame(this.controlFrame);
    this.controlFrame = requestAnimationFrame(() => this.updateControls());
  }

  updateControls() {
    if (this.hasAttribute("aria-busy")) {
      if (this.previousButton) this.previousButton.disabled = true;
      if (this.nextButton) this.nextButton.disabled = true;
      return;
    }

    const maxScroll = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
    const atStart = this.viewport.scrollLeft <= 2;
    const atEnd = this.viewport.scrollLeft >= maxScroll - 2;
    const hasOverflow = maxScroll > 2;

    if (this.previousButton) this.previousButton.disabled = !hasOverflow || atStart;
    if (this.nextButton) this.nextButton.disabled = !hasOverflow || atEnd;
    this.updateActiveSlide();
  }

  updateActiveSlide() {
    if (this.dataset.cardLayout !== "cherished") return;

    const viewportRect = this.viewport.getBoundingClientRect();
    const edge = parseFloat(getComputedStyle(this.track).paddingInlineStart) || 0;
    const isRtl = this.dataset.direction === "rtl";
    let activeSlide;
    let closestDistance = Number.POSITIVE_INFINITY;

    this.slides.forEach((slide) => {
      const rect = slide.getBoundingClientRect();
      const distance = isRtl
        ? Math.abs(viewportRect.right - rect.right - edge)
        : Math.abs(rect.left - viewportRect.left - edge);

      if (distance < closestDistance) {
        closestDistance = distance;
        activeSlide = slide;
      }
    });

    this.slides.forEach((slide) => {
      slide.classList.toggle("is-active", slide === activeSlide);
    });
  }

  setInitialPosition() {
    requestAnimationFrame(() => {
      const maxScroll = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
      const allowInitialPeek = this.dataset.initialPeekDesktopOnly !== "true" ||
        window.matchMedia("(min-width: 750px)").matches;
      const startOffset = this.dataset.initialPeek === "true" && allowInitialPeek ? this.step : 0;
      this.viewport.scrollLeft =
        this.dataset.direction === "rtl"
          ? Math.max(0, maxScroll - startOffset)
          : Math.min(maxScroll, startOffset);
      this.updateControls();
    });
  }

  async initializeProducts() {
    if (this.dataset.useJsonSearch === "true") {
      await this.loadJsonProducts();
    } else {
      await this.applyRemoteOrder();
    }

    if (this.dataset.direction === "rtl") {
      this.slides.reverse().forEach((slide) => this.track.append(slide));
    }

    this.removeAttribute("aria-busy");
    this.setInitialPosition();
  }

  async loadJsonProducts() {
    const useTagCollection = this.dataset.useTagCollection === "true";
    const tagCollectionHandle = this.dataset.tagCollectionHandle || this.dataset.featuredTagHandle;
    const query = this.buildTagQuery();
    if (!query || (useTagCollection && !tagCollectionHandle)) {
      this.track.replaceChildren();
      return false;
    }

    const limit = Number(this.dataset.productLimit) || 50;
    const url = useTagCollection
      ? new URL(this.dataset.sourceUrl || "/collections/all-products", location.origin)
      : new URL(this.dataset.searchRoot || "/search", location.origin);
    if (useTagCollection) {
      url.pathname = `${url.pathname.replace(/\/+$/, "")}/${tagCollectionHandle}`;
    } else {
      url.searchParams.set("q", query);
      url.searchParams.set("type", "product");
      url.searchParams.set("options[prefix]", "last");
    }
    url.searchParams.set("view", "clay-showcase");
    if (this.dataset.sortKey === "UPDATED_AT") {
      url.searchParams.set("sort_by", "created-descending");
    } else if (useTagCollection && this.dataset.sortKey === "BEST_SELLING") {
      url.searchParams.set("sort_by", "best-selling");
    } else if (!useTagCollection) {
      url.searchParams.set("sort_by", "relevance");
    }

    try {
      const response = await fetch(url, {
        credentials: "same-origin",
        // Shopify's native collection JSON takes precedence over the Liquid view
        // when this request advertises application/json.
        headers: { Accept: "text/html" },
      });
      if (!response.ok) throw new Error();

      const payload = await response.json();
      if (!Array.isArray(payload.products)) {
        throw new Error("Shopify did not render the clay-showcase product view");
      }
      const slides = this.filterRemoteProducts(payload.products)
        .slice(0, limit)
        .map((product, index) => this.createSlide(product, index))
        .filter(Boolean);
      if (!slides.length) {
        this.track.replaceChildren();
        this.updateControls();
        return false;
      }

      this.track.replaceChildren(...slides);
      this.updateControls();
      return true;
    } catch (error) {
      console.error("[Clay showcase] Could not load products", url.toString(), error);
      this.track.replaceChildren();
      this.updateControls();
      return false;
    }
  }

  filterRemoteProducts(products) {
    if (!Array.isArray(products)) return [];
    const normalize = (value) => String(value || "").trim().toLocaleLowerCase();
    const featuredTag = this.dataset.featuredEnabled === "true"
      ? normalize(this.dataset.featuredTag)
      : "";
    const customTags = this.dataset.customTagsEnabled === "true"
      ? this.dataset.customTags.split(",").map(normalize).filter(Boolean)
      : [];

    return products.filter((product) => {
      const tags = new Set((product.tags || []).map(normalize));
      if (featuredTag && !tags.has(featuredTag)) return false;
      if (!customTags.length) return true;
      return this.dataset.matchAllTags === "true"
        ? customTags.every((tag) => tags.has(tag))
        : customTags.some((tag) => tags.has(tag));
    });
  }

  buildTagQuery() {
    const featuredTag =
      this.dataset.featuredEnabled === "true"
        ? this.dataset.featuredTag.trim()
        : "";
    const customTags =
      this.dataset.customTagsEnabled === "true"
        ? this.dataset.customTags.split(",")
        : [];
    const uniqueCustomTags = [...new Map(
      customTags
        .map((tag) => tag.trim())
        .filter(Boolean)
        .map((tag) => [tag.toLocaleLowerCase(), tag])
    ).values()];
    const formatTag = (tag) =>
      `tag:"${tag.replaceAll("\\", "\\\\").replaceAll('"', '\\"')}"`;
    const featuredTerm = featuredTag ? formatTag(featuredTag) : "";
    const customTerms = uniqueCustomTags
      .filter(
        (tag) => tag.toLocaleLowerCase() !== featuredTag.toLocaleLowerCase()
      )
      .map(formatTag);

    if (!featuredTerm) {
      return customTerms.join(
        this.dataset.matchAllTags === "true" ? " AND " : " OR "
      );
    }
    if (!customTerms.length) return featuredTerm;
    if (this.dataset.matchAllTags === "true") {
      return [featuredTerm, ...customTerms].join(" AND ");
    }
    return `${featuredTerm} AND (${customTerms.join(" OR ")})`;
  }

  createSlide(product, index) {
    if (this.dataset.cardLayout === "cherished") {
      return this.createCherishedSlide(product, index);
    }

    if (!product?.url || !product.title || !product.handle) return null;

    const article = document.createElement("article");
    article.className = "cc-showcase-card";
    article.dataset.showcaseSlide = "";
    article.dataset.productHandle = product.handle;
    article.dir = this.dataset.direction;
    article.setAttribute("role", "listitem");

    const productLink = document.createElement("a");
    productLink.className = "cc-showcase-card__product-link";
    productLink.href = product.url;
    productLink.setAttribute("aria-label", product.title);
    productLink.draggable = false;

    if (product.image?.src) {
      const showcaseImage = document.createElement("img");
      const widths = [240, 320, 400, 480, 600, 720, 900, 1000, 1200];
      showcaseImage.className = "cc-showcase-card__image";
      showcaseImage.src = product.image.src;
      showcaseImage.srcset = widths
        .map((width) => `${this.withImageWidth(product.image.src, width)} ${width}w`)
        .join(", ");
      showcaseImage.sizes = this.dataset.imageSizes;
      showcaseImage.alt = product.image.alt || product.title;
      showcaseImage.width = product.image.width || 1200;
      showcaseImage.height = product.image.height || 1200;
      showcaseImage.loading = index < 4 ? "eager" : "lazy";
      showcaseImage.fetchPriority = index < 4 ? "high" : "auto";
      showcaseImage.decoding = "async";
      showcaseImage.draggable = false;
      productLink.append(showcaseImage);
    }

    const title = document.createElement("span");
    title.className = "cc-showcase-card__title";
    title.textContent = product.title;
    productLink.append(title);
    article.append(productLink);

    if (product.brand?.name) {
      const brand = document.createElement(product.brand.url ? "a" : "span");
      brand.className = "cc-showcase-card__brand";
      brand.textContent = product.brand.name;
      if (product.brand.url) brand.href = product.brand.url;
      article.append(brand);
    }

    return article;
  }

  createCherishedSlide(product, index) {
    if (!product?.url || !product.title || !product.handle) return null;

    const article = document.createElement("article");
    article.className = "cc-cherished-card";
    article.dataset.showcaseSlide = "";
    article.dataset.productHandle = product.handle;
    article.dir = this.dataset.direction;
    article.setAttribute("role", "listitem");

    const productLink = document.createElement("a");
    productLink.className = "cc-cherished-card__product-link";
    productLink.href = product.url;
    productLink.setAttribute("aria-label", product.title);
    productLink.draggable = false;

    if (product.image?.src) {
      const image = document.createElement("img");
      const widths = [320, 480, 600, 720, 900, 1000, 1200, 1400];
      image.className = "cc-cherished-card__image";
      image.src = product.image.src;
      image.srcset = widths
        .map((width) => `${this.withImageWidth(product.image.src, width)} ${width}w`)
        .join(", ");
      image.sizes = this.dataset.imageSizes;
      image.alt = product.image.alt || product.title;
      image.width = product.image.width || 1200;
      image.height = product.image.height || 1600;
      image.loading = index < 3 ? "eager" : "lazy";
      image.fetchPriority = index < 3 ? "high" : "auto";
      image.decoding = "async";
      image.draggable = false;
      productLink.append(image);
    }

    article.append(productLink);

    const information = document.createElement("div");
    information.className = "cc-cherished-card__information";

    const arrow = document.createElement("a");
    arrow.className = "cc-cherished-card__arrow";
    arrow.href = product.url;
    arrow.setAttribute("aria-label", product.title);
    arrow.innerHTML = '<svg aria-hidden="true" viewBox="0 0 24 24"><path d="M5 19 19 5M9 5h10v10"/></svg>';
    information.append(arrow);

    if (product.brand?.name) {
      const brand = document.createElement(product.brand.url ? "a" : "span");
      brand.className = "cc-cherished-card__brand";
      brand.textContent = product.brand.name;
      if (product.brand.url) brand.href = product.brand.url;
      information.append(brand);
    }

    const title = document.createElement("a");
    title.className = "cc-cherished-card__title";
    title.href = product.url;
    title.textContent = product.title;
    information.append(title);
    article.append(information);

    return article;
  }

  withImageWidth(source, width) {
    const url = new URL(source, location.origin);
    url.searchParams.set("width", width);
    return url.toString();
  }

  async applyRemoteOrder() {
    if (!this.dataset.sortKey) return;

    const isAllProducts = this.dataset.sourceHandle === "all";
    if (!isAllProducts && this.dataset.sortKey !== "BEST_SELLING") return;

    const query = isAllProducts
      ? `query ClayShowcase($sortKey: ProductSortKeys!, $reverse: Boolean!, $query: String) {
          products(first: 50, sortKey: $sortKey, reverse: $reverse, query: $query) {
            nodes { handle }
          }
        }`
      : `query ClayShowcaseCollection($handle: String!) {
          collection(handle: $handle) {
            products(first: 50, sortKey: BEST_SELLING) {
              nodes { handle }
            }
          }
        }`;

    const variables = isAllProducts
      ? {
          sortKey: this.dataset.sortKey,
          reverse: this.dataset.sortReverse === "true",
          query: this.buildTagQuery() || null,
        }
      : { handle: this.dataset.sourceHandle };

    try {
      const response = await fetch("/api/2026-07/graphql.json", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          query,
          variables,
        }),
      });
      if (!response.ok) return;

      const payload = await response.json();
      const products = isAllProducts
        ? payload.data?.products?.nodes
        : payload.data?.collection?.products?.nodes;
      const handles = products?.map((product) => product.handle);
      if (!handles?.length) return;

      const order = new Map(handles.map((handle, index) => [handle, index]));
      const slides = this.slides.sort((first, second) => {
        const firstIndex = order.get(first.dataset.productHandle) ?? Number.MAX_SAFE_INTEGER;
        const secondIndex = order.get(second.dataset.productHandle) ?? Number.MAX_SAFE_INTEGER;
        return firstIndex - secondIndex;
      });

      slides.forEach((slide) => this.track.append(slide));
    } catch {}
  }
}

if (!customElements.get("clay-product-showcase")) {
  customElements.define("clay-product-showcase", ClayProductShowcase);
}
